import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BasicInfoSection } from '@/components/propfirm-editor/BasicInfoSection';
import { ChallengesSection } from '@/components/propfirm-editor/ChallengesSection';
import { FeaturesSection } from '@/components/propfirm-editor/FeaturesSection';
import { ProsConsSection } from '@/components/propfirm-editor/ProsConsSection';
import { CouponsSection } from '@/components/propfirm-editor/CouponsSection';
import { SEOSection } from '@/components/propfirm-editor/SEOSection';
import { SidebarSection } from '@/components/propfirm-editor/SidebarSection';
import {
  createPropFirm,
  updatePropFirm,
  getPropFirmById,
} from '@/services/propFirmApi';

const LOGO_BASE_URL = 'https://prop-api.8100081.xyz/prop-firms/firmlogo';

const dataURLtoFile = (dataUrl, filename = 'logo.jpg') => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  const ext = mime.split('/')[1] || 'jpg';
  return new File(
    [u8arr],
    filename.endsWith(`.${ext}`) ? filename : `logo.${ext}`,
    { type: mime }
  );
};

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (
    logoUrl.startsWith('http://') ||
    logoUrl.startsWith('https://') ||
    logoUrl.startsWith('data:')
  ) {
    return logoUrl;
  }
  return `${LOGO_BASE_URL}/${logoUrl}`;
};

export const PropFirmEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState(null);
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');
  const [country, setCountry] = useState('');
  const [foundedYear, setFoundedYear] = useState('');
  const [website, setWebsite] = useState('');
  const [affiliateUrl, setAffiliateUrl] = useState('');
  const [status, setStatus] = useState('draft');
  const [featured, setFeatured] = useState(false);
  const [challengeTypes, setChallengeTypes] = useState([]);
  const [pros, setPros] = useState([]);
  const [cons, setCons] = useState([]);
  const [allowedRules, setAllowedRules] = useState([]);
  const [notAllowedRules, setNotAllowedRules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [featurePriority, setFeaturePriority] = useState(1);

  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Load firm when editing (same idea as blog)
  useEffect(() => {
    if (!id) return;

    const loadFirm = async () => {
      try {
        setLoading(true);
        const res = await getPropFirmById(id);
        const data = res?.data || res;

        if (!data) {
          toast.error('Firm not found');
          navigate('/dashboard/firms');
          return;
        }

        setName(data.name || '');
        setSlug(data.slug || '');
        setLogo(getLogoUrl(data.logo_url || data.logo) || null);
        setTagline(data.tagline || '');
        setDescription(data.description || '');
        setCountry(data.country || '');
        setFoundedYear(
          data.founded_year?.toString() ||
            data.foundedYear?.toString() ||
            ''
        );
        setWebsite(data.website || data.website_url || '');
        setAffiliateUrl(data.affiliate_url || data.affiliateUrl || '');
        setStatus((data.status || 'draft').toLowerCase());
        setFeatured(!!data.featured || !!data.featured_order);
        setFeaturePriority(
          data.feature_priority || data.featured_order || data.featurePriority || 1
        );
        setMetaTitle(data.meta_title || '');
        setMetaDescription(data.meta_description || '');

        setPlatforms(
          (data.platforms || []).map((p) =>
            typeof p === 'string' ? { id: generateId(), value: p } : { id: generateId(), value: p.value || p.name || '' }
          )
        );
        setInstruments(
          (data.instruments || []).map((i) =>
            typeof i === 'string' ? { id: generateId(), value: i } : { id: generateId(), value: i.value || i.name || '' }
          )
        );
        setPaymentMethods(
          (data.payment_methods || data.paymentMethods || []).map((m) =>
            typeof m === 'string' ? { id: generateId(), value: m } : { id: generateId(), value: m.value || m.name || '' }
          )
        );

        const prosCons = data.pros_cons || [];
        setPros(
          prosCons
            .filter((x) => x.type === 'pro')
            .map((x) => ({ id: generateId(), value: x.text || '' }))
        );
        setCons(
          prosCons
            .filter((x) => x.type === 'con')
            .map((x) => ({ id: generateId(), value: x.text || '' }))
        );

        const features = data.features || [];
        setAllowedRules(
          features
            .filter((x) => x.type === 'allowed')
            .map((x) => ({ id: generateId(), value: x.text || '' }))
        );
        setNotAllowedRules(
          features
            .filter((x) => x.type === 'not_allowed')
            .map((x) => ({ id: generateId(), value: x.text || '' }))
        );

        setFaqs(
          (data.faqs || []).map((f) => ({
            id: generateId(),
            question: f.question || '',
            answer: f.answer || '',
          }))
        );

        setCoupons(
          (data.discounts || data.coupons || []).map((c) => ({
            id: generateId(),
            code: c.discount_code || c.code || '',
            discount: c.discount_text || c.discount || '',
          }))
        );

        setChallengeTypes(
          (data.challenges || []).map((c) => ({
            id: generateId(),
            name: c.name || '',
            price: c.price?.toString() || '',
            maxFunding: c.max_funding?.toString() || c.maxFunding?.toString() || '',
            profitSplit: c.profit_split?.toString() || c.profitSplit?.toString() || '',
            steps: c.steps?.toString() || '2',
            drawdownType: c.drawdown_type || c.drawdownType || 'Trailing',
            maxDrawdown: c.max_drawdown?.toString() || '',
            dailyDrawdown: c.daily_drawdown?.toString() || '',
            leverage: c.leverage || '100:1',
            minTradingDays: c.min_trading_days?.toString() || '',
            payoutFrequency: c.payout_frequency || '14D',
            profitTarget: c.profit_target || c.profitTarget || '',
            payoutCycle: c.payout_cycle || c.payoutCycle || '',
            frequency: c.frequency || 'Always Available',
            affiliateLink: c.affiliate_link || '',
          }))
        );
      } catch (error) {
        console.error(error);
        toast.error('Failed to load firm');
        navigate('/dashboard/firms');
      } finally {
        setLoading(false);
      }
    };

    loadFirm();
  }, [id, navigate]);

  const addChallengeType = () => {
    setChallengeTypes([
      ...challengeTypes,
      {
        id: Date.now(),
        name: '',
        price: '',
        maxFunding: '',
        profitSplit: '',
        steps: '2',
        drawdownType: 'Trailing',
        maxDrawdown: '',
        dailyDrawdown: '',
        leverage: '100:1',
        minTradingDays: '',
        payoutFrequency: '14D',
        profitTarget: '',
        payoutCycle: '',
        frequency: 'Always Available',
        affiliateLink: '',
      },
    ]);
  };

  const removeChallengeType = (challengeId) => {
    setChallengeTypes(challengeTypes.filter((c) => c.id !== challengeId));
  };

  const updateChallengeType = (challengeId, field, value) => {
    setChallengeTypes(
      challengeTypes.map((c) =>
        c.id === challengeId ? { ...c, [field]: value } : c
      )
    );
  };

  const addItem = (list, setList) => {
    setList([...list, { id: generateId(), value: '' }]);
  };

  const updateItem = (list, setList, itemId, value) => {
    setList(list.map((item) => (item.id === itemId ? { ...item, value } : item)));
  };

  const removeItem = (list, setList, itemId) => {
    setList(list.filter((item) => item.id !== itemId));
  };

  const addFaq = () => {
    setFaqs([...faqs, { id: generateId(), question: '', answer: '' }]);
  };

  const updateFaq = (faqId, field, value) => {
    setFaqs(
      faqs.map((faq) => (faq.id === faqId ? { ...faq, [field]: value } : faq))
    );
  };

  const removeFaq = (faqId) => {
    setFaqs(faqs.filter((faq) => faq.id !== faqId));
  };

  const handleSave = async () => {
    try {
      if (!name.trim()) {
        toast.error('Firm name is required');
        return;
      }

      setSaving(true);

      const payload = {
        ...(isEdit ? { id } : {}),
        name,
        slug,
        status: status.charAt(0).toUpperCase() + status.slice(1),
        tagline,
        description,
        country,
        founded_year: foundedYear ? Number(foundedYear) : null,
        website,
        affiliate_url: affiliateUrl,
        featured,
        feature_priority: featurePriority,
        meta_title: metaTitle,
        meta_description: metaDescription,
        platforms: platforms.map((p) => p.value).filter(Boolean),
        instruments: instruments.map((i) => i.value).filter(Boolean),
        payment_methods: paymentMethods.map((m) => m.value).filter(Boolean),
        pros_cons: [
          ...pros
            .filter((item) => item.value?.trim())
            .map((item) => ({ type: 'pro', text: item.value })),
          ...cons
            .filter((item) => item.value?.trim())
            .map((item) => ({ type: 'con', text: item.value })),
        ],
        faqs: faqs
          .filter((f) => f.question && f.answer)
          .map((f) => ({ question: f.question, answer: f.answer })),
        discounts: coupons
          .filter((c) => c.code && c.discount)
          .map((c) => ({
            discount_code: c.code,
            discount_text: c.discount,
          })),
        features: [
          ...allowedRules
            .filter((r) => r.value)
            .map((r) => ({ type: 'allowed', text: r.value })),
          ...notAllowedRules
            .filter((r) => r.value)
            .map((r) => ({ type: 'not_allowed', text: r.value })),
        ],
        challenges: challengeTypes.map((challenge) => ({
          name: challenge.name,
          price: Number(challenge.price) || 0,
          max_funding: Number(challenge.maxFunding) || 0,
          profit_split: Number(challenge.profitSplit) || 0,
          profit_target: challenge.profitTarget,
          payout_cycle: challenge.payoutCycle,
          frequency: challenge.frequency,
        })),
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));

      if (logo instanceof File) {
        formData.append('logo', logo);
      } else if (typeof logo === 'string' && logo.startsWith('data:')) {
        formData.append('logo', dataURLtoFile(logo, 'logo.jpg'));
      }
      // existing http logo URL → don't re-upload

      if (isEdit) {
        await updatePropFirm(formData);
        toast.success('Firm updated successfully');
      } else {
        await createPropFirm(formData);
        toast.success('Firm created successfully');
      }

      navigate('/dashboard/firms');
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Unable to save firm'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Prop Firm' : 'Add Prop Firm'}
        breadcrumbs={['Admin', 'Content', 'Prop Firms', isEdit ? 'Edit' : 'New']}
        actions={
          <Button variant="outline" onClick={() => navigate('/dashboard/firms')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Firms
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Tabs defaultValue="basic" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="challenges">Challenges</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="pros-cons">Pros & Cons</TabsTrigger>
              <TabsTrigger value="coupons">Coupons</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>

            <TabsContent value="basic">
              <BasicInfoSection
                name={name}
                setName={setName}
                slug={slug}
                setSlug={setSlug}
                logo={logo}
                setLogo={setLogo}
                tagline={tagline}
                setTagline={setTagline}
                description={description}
                setDescription={setDescription}
                country={country}
                setCountry={setCountry}
                foundedYear={foundedYear}
                setFoundedYear={setFoundedYear}
                website={website}
                setWebsite={setWebsite}
                affiliateUrl={affiliateUrl}
                setAffiliateUrl={setAffiliateUrl}
                platforms={platforms}
                setPlatforms={setPlatforms}
                instruments={instruments}
                setInstruments={setInstruments}
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                generateId={generateId}
              />
            </TabsContent>

            <TabsContent value="challenges">
              <ChallengesSection
                challengeTypes={challengeTypes}
                setChallengeTypes={setChallengeTypes}
                updateChallengeType={updateChallengeType}
                removeChallengeType={removeChallengeType}
                addChallengeType={addChallengeType}
              />
            </TabsContent>

            <TabsContent value="features">
              <FeaturesSection
                allowedRules={allowedRules}
                setAllowedRules={setAllowedRules}
                notAllowedRules={notAllowedRules}
                setNotAllowedRules={setNotAllowedRules}
                addItem={addItem}
                updateItem={updateItem}
                removeItem={removeItem}
              />
            </TabsContent>

            <TabsContent value="pros-cons">
              <ProsConsSection
                pros={pros}
                setPros={setPros}
                cons={cons}
                setCons={setCons}
                addItem={addItem}
                updateItem={updateItem}
                removeItem={removeItem}
              />
            </TabsContent>

            <TabsContent value="coupons">
              <CouponsSection
                coupons={coupons}
                setCoupons={setCoupons}
                generateId={generateId}
              />
            </TabsContent>

            <TabsContent value="seo">
              <SEOSection
                metaTitle={metaTitle}
                setMetaTitle={setMetaTitle}
                metaDescription={metaDescription}
                setMetaDescription={setMetaDescription}
                faqs={faqs}
                addFaq={addFaq}
                updateFaq={updateFaq}
                removeFaq={removeFaq}
              />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <SidebarSection
            id={id}
            status={status}
            setStatus={setStatus}
            featured={featured}
            setFeatured={setFeatured}
            featurePriority={featurePriority}
            setFeaturePriority={setFeaturePriority}
            handleSave={handleSave}
            saving={saving}
          />
        </div>
      </div>
    </div>
  );
};